import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '../../components/ui/button';
import QuizForm from '../../components/teacher/QuizForm';
import type { Quiz } from '../../types';
import { api, useQuery, useMutation } from '../../lib/convex';
import type { Id } from '../../lib/convex';
import { TeacherAuth, SignOut } from '../../components/auth/TeacherAuth';

const EditQuizPage: React.FC = () => {
  return (
    <TeacherAuth>
      <EditQuizContent />
    </TeacherAuth>
  );
};

const EditQuizContent: React.FC = () => {
  const navigate = useNavigate();
  const { quizId } = useParams({ from: '/teacher/quizzes/$quizId' });
  
  // Set up the query to get the quiz
  const quiz = useQuery(api.quizzes.get, { 
    id: quizId as Id<"quizzes">
  });
  
  // Set up the mutation to update the quiz
  const updateQuiz = useMutation(api.quizzes.update);

  const handleUpdateQuiz = async (updatedQuiz: Quiz) => {
    try {
      // Update the quiz in the database
      await updateQuiz({
        id: quizId as Id<"quizzes">,
        title: updatedQuiz.title,
        description: updatedQuiz.description,
        questions: updatedQuiz.questions.map(q => ({
          text: q.text,
          timeLimit: q.timeLimit,
          pointValue: q.pointValue,
          options: q.options
        }))
      });

      // Navigate back to the teacher dashboard
      navigate({ to: '/teacher' });
    } catch (error) {
      console.error('Error updating quiz:', error);
      alert('Failed to update quiz. Please try again.');
    }
  };

  // Show loading state while fetching the quiz
  if (!quiz) {
    return (
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Edit Quiz</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate({ to: '/teacher' })}>
            Cancel
          </Button>
          <SignOut />
        </div>
      </div>

      <QuizForm 
        initialQuiz={quiz as Quiz}
        onSubmit={handleUpdateQuiz}
        isEditing={true}
      />
    </div>
  );
};

export default EditQuizPage;